/**
 * Feature Flags Management
 * 
 * Centralized feature flag configuration and utilities for the application.
 * Supports environment-based flags and user cohort-based flags.
 */

// Import version info for beta detection
let versionInfo: any = {};
try {
  const version = require('@/app/version');
  versionInfo = version;
} catch (error) {
  // Fallback for development
  versionInfo = {
    IS_BETA: import.meta.env.VITE_BETA === 'true',
    FEATURE_FLAGS: {}
  };
}

export type FeatureFlagKey = 
  | 'whispercoach_v1'
  | 'telemetry_debug'
  | 'new_library'
  | 'enhanced_analysis'
  | 'progress_events'
  | 'feedback_modal'
  | 'mux_reconcile'
  | 'capture_machine'
  | 'analysis_persistence'
  | 'beta_banner'
  | 'performance_metrics'
  | 'error_boundary_enhanced'
  | 'clubhouse_feed' // P05-1: Community Feed MVP
  | 'social_actions_icons' // P05-1b: Social Actions Bar (Like, Comment, Share, Favorite)
  | 'course_admin' // P06-01: Course Claim & Admin Access
  | 'badges_admin' // P05-07: Admin Badge Management System
  | 'zip_code_radius_search'; // P05-5-6: ZIP Code Radius Coach Search

/**
 * Default feature flag configuration
 */
export const DEFAULT_FEATURE_FLAGS: Record<FeatureFlagKey, boolean> = {
  // Premium features (disabled by default)
  whispercoach_v1: false,
  
  // Beta-only features
  telemetry_debug: versionInfo.IS_BETA || false,
  feedback_modal: versionInfo.IS_BETA || false,
  beta_banner: versionInfo.IS_BETA || false,
  performance_metrics: versionInfo.IS_BETA || false,
  error_boundary_enhanced: versionInfo.IS_BETA || false,
  
  // Always enabled features
  new_library: true,
  enhanced_analysis: true,
  progress_events: true,
  mux_reconcile: true,
  capture_machine: true,
  analysis_persistence: true,
  
  // P05-1: Community Feed MVP (enabled by default)
  clubhouse_feed: true,
  
  // P05-1b: Social Actions Bar (enabled by default, purely additive)
  social_actions_icons: true,
  
  // P06-01: Course Claim & Admin Access (enabled by default)
  course_admin: true,
  
  // P05-07: Admin Badge Management System (enabled by default)
  badges_admin: true,
  
  // P05-5-6: ZIP Code Radius Coach Search (opt-in, disabled by default)
  zip_code_radius_search: false
};

/**
 * Get feature flag value with fallback to default
 */
export function getFeatureFlag(flag: FeatureFlagKey): boolean {
  // Check version info first (from build script)
  if (versionInfo.FEATURE_FLAGS && typeof versionInfo.FEATURE_FLAGS[flag] === 'boolean') {
    return versionInfo.FEATURE_FLAGS[flag];
  }
  
  // Check environment variables (Vite uses VITE_ prefix)
  const viteEnvKey = `VITE_ENABLE_${flag.toUpperCase()}`;
  const viteEnvValue = (import.meta.env as any)[viteEnvKey];
  if (viteEnvValue !== undefined) {
    return viteEnvValue === 'true';
  }
  
  // Also check NEXT_PUBLIC_ prefix for compatibility
  const nextEnvKey = `NEXT_PUBLIC_ENABLE_${flag.toUpperCase()}`;
  const nextEnvValue = (import.meta.env as any)[nextEnvKey];
  if (nextEnvValue !== undefined) {
    return nextEnvValue === 'true';
  }
  
  // Check localStorage override (development only)
  if (typeof window !== 'undefined' && import.meta.env.DEV) {
    const localStorageKey = `feature_${flag}`;
    const localValue = localStorage.getItem(localStorageKey);
    if (localValue !== null) {
      return localValue === 'true';
    }
  }
  
  // Fallback to default
  return DEFAULT_FEATURE_FLAGS[flag];
}

/**
 * Get all feature flags as an object
 */
export function getAllFeatureFlags(): Record<FeatureFlagKey, boolean> {
  const flags: Record<FeatureFlagKey, boolean> = {} as any;
  
  Object.keys(DEFAULT_FEATURE_FLAGS).forEach(key => {
    flags[key as FeatureFlagKey] = getFeatureFlag(key as FeatureFlagKey);
  });
  
  return flags;
}

/**
 * Check if a feature is enabled
 */
export function isFeatureEnabled(flag: FeatureFlagKey): boolean {
  return getFeatureFlag(flag);
}

/**
 * Get enabled feature flags (for debugging/analytics)
 */
export function getEnabledFeatures(): FeatureFlagKey[] {
  return Object.keys(DEFAULT_FEATURE_FLAGS).filter(key => 
    getFeatureFlag(key as FeatureFlagKey)
  ) as FeatureFlagKey[];
}

/**
 * React hook for feature flags
 */
export function useFeatureFlag(flag: FeatureFlagKey): boolean {
  return getFeatureFlag(flag);
}

/**
 * React hook for multiple feature flags
 */
export function useFeatureFlags(): Record<FeatureFlagKey, boolean> {
  return getAllFeatureFlags();
}

/**
 * Development utilities for testing feature flags
 */
export const devUtils = {
  /**
   * Override feature flag in localStorage (development only)
   */
  setFeatureFlag(flag: FeatureFlagKey, enabled: boolean) {
    if (!import.meta.env.DEV) {
      console.warn('Feature flag overrides only work in development');
      return;
    }
    
    if (typeof window === 'undefined') return;
    
    const key = `feature_${flag}`;
    if (enabled) {
      localStorage.setItem(key, 'true');
    } else {
      localStorage.setItem(key, 'false');
    }
    
    console.log(`🚩 Feature flag override: ${flag} = ${enabled}`);
  },
  
  /**
   * Clear all feature flag overrides
   */
  clearOverrides() {
    if (!import.meta.env.DEV) return;
    if (typeof window === 'undefined') return;
    
    Object.keys(DEFAULT_FEATURE_FLAGS).forEach(flag => {
      localStorage.removeItem(`feature_${flag}`);
    });
    
    console.log('🚩 Cleared all feature flag overrides');
  },
  
  /**
   * Log current feature flag status
   */
  logFeatureFlags() {
    console.log('🚩 Current Feature Flags:', getAllFeatureFlags());
  }
};

// Expose dev utils globally in development
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).__featureFlags__ = devUtils;
}

/**
 * Feature flag configuration for different environments
 */
export const ENVIRONMENT_CONFIGS = {
  development: {
    whispercoach_v1: false,
    telemetry_debug: true,
    feedback_modal: true,
    beta_banner: true,
    performance_metrics: true
  },
  
  beta: {
    whispercoach_v1: false,
    telemetry_debug: true,
    feedback_modal: true,
    beta_banner: true,
    performance_metrics: true,
    error_boundary_enhanced: true
  },
  
  production: {
    whispercoach_v1: false, // Will be enabled post-beta
    telemetry_debug: false,
    feedback_modal: false,
    beta_banner: false,
    performance_metrics: false,
    error_boundary_enhanced: false
  }
};

/**
 * Get feature flags for a specific environment
 */
export function getEnvironmentFlags(environment: 'development' | 'beta' | 'production') {
  return {
    ...DEFAULT_FEATURE_FLAGS,
    ...ENVIRONMENT_CONFIGS[environment]
  };
}
